/**
 * Canonical monthly list prices for Budgit Premium.
 *
 * Apple IAP and Stripe differ on purpose (Apple's cut). #1886 (merged
 * 2026-10-13, commit 6ed5084a0) set these from Jared's D22 (2026-10-12):
 * Apple $7.99/mo, Stripe/web $4.99/mo.
 *
 * Backend MRR fallbacks re-export the cents. The marketing site imports the
 * derived dollar strings. ux/public/index.html keeps tokens, and the webpack
 * build fills them by calling applyWebsitePricingHtml. Do not copy a dollar
 * amount into the site, and do not require node builtins here: the web client
 * imports this file.
 */

const DEFAULT_APPLE_PREMIUM_CENTS = 799;
const DEFAULT_STRIPE_PREMIUM_CENTS = 499;

const APPLE_MONTHLY_USD_TOKEN = '__APPLE_MONTHLY_USD__';
const STRIPE_MONTHLY_USD_TOKEN = '__STRIPE_MONTHLY_USD__';

function usdFromCents(cents) {
  if (!Number.isInteger(cents) || cents < 0) {
    throw new Error(`subscription price cents must be a non-negative integer, got ${cents}`);
  }
  const dollars = Math.floor(cents / 100);
  const remainder = cents % 100;
  return `${dollars}.${String(remainder).padStart(2, '0')}`;
}

const APPLE_MONTHLY_USD = usdFromCents(DEFAULT_APPLE_PREMIUM_CENTS);
const STRIPE_MONTHLY_USD = usdFromCents(DEFAULT_STRIPE_PREMIUM_CENTS);

const DEFAULT_MONTHLY_PRICE_CENTS = Object.freeze({
  'app.budgit.premium.monthly': DEFAULT_APPLE_PREMIUM_CENTS,
});

function applyWebsitePricingHtml(html) {
  const hasApple = html.includes(APPLE_MONTHLY_USD_TOKEN);
  const hasStripe = html.includes(STRIPE_MONTHLY_USD_TOKEN);
  if (!hasApple && !hasStripe) {
    return html;
  }
  if (!hasApple || !hasStripe) {
    throw new Error(
      'website pricing HTML must include both __APPLE_MONTHLY_USD__ and __STRIPE_MONTHLY_USD__'
    );
  }
  return html
    .split(APPLE_MONTHLY_USD_TOKEN)
    .join(APPLE_MONTHLY_USD)
    .split(STRIPE_MONTHLY_USD_TOKEN)
    .join(STRIPE_MONTHLY_USD);
}

/**
 * Dollar amounts written into marketing source, plus quoted x.xx literals
 * (JSON-LD "price": "9.99", or a config fallback '7.99'). $0 is the free tier
 * and is not a literal this flags.
 */
function findHardCodedPriceLiterals(source) {
  const hits = [];
  const patterns = [/\$\d+\.\d{2}/g, /["']\d+\.\d{2}["']/g];
  for (const pattern of patterns) {
    const matches = source.match(pattern);
    if (matches) {
      hits.push(...matches);
    }
  }
  return hits;
}

module.exports = {
  DEFAULT_APPLE_PREMIUM_CENTS,
  DEFAULT_STRIPE_PREMIUM_CENTS,
  DEFAULT_MONTHLY_PRICE_CENTS,
  APPLE_MONTHLY_USD,
  STRIPE_MONTHLY_USD,
  APPLE_MONTHLY_USD_TOKEN,
  STRIPE_MONTHLY_USD_TOKEN,
  usdFromCents,
  applyWebsitePricingHtml,
  findHardCodedPriceLiterals,
};
